import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  MenuItem,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Grid,
  Divider,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import { useTheme } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';

function Members() {
  const theme = useTheme();
  const [members, setMembers] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [searchField, setSearchField] = useState('name');
  const [selectedMember, setSelectedMember] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  useEffect(() => {
    fetchMembers();
  }, []);

  const fetchMembers = async () => {
    try {
      setLoading(true);
      const querySnapshot = await getDocs(collection(db, 'members'));
      const membersData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setMembers(membersData);
    } catch (error) {
      console.error('Error fetching members:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleRowClick = (member) => {
    setSelectedMember(member);
    setDetailsOpen(true);
  };

  const maskEnrollmentNo = (enrollmentNo) => {
    if (!enrollmentNo) return '';
    const parts = enrollmentNo.split('/');
    if (parts.length === 3) {
      return `${parts[0]}/XXXX/${parts[2].slice(0, 2)}XX`;
    }
    return enrollmentNo;
  };

  const filteredMembers = members.filter(member =>
    member[searchField]?.toString().toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Box 
      sx={{ 
        flexGrow: 1,
        width: '100%',
        minHeight: 'calc(100vh - 64px)',
        bgcolor: '#f8f9fa',
        pt: { xs: 3, sm: 4 },
        pb: { xs: 3, sm: 4 }
      }}
    >
      <Container maxWidth="xl">
        {/* Header */}
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Typography 
            variant="h4" 
            component="h1" 
            sx={{ 
              fontWeight: 600,
              color: '#580100',
              mb: 1
            }}
          >
            Our Members
          </Typography>
          <Typography 
            variant="subtitle1" 
            color="text.secondary"
            sx={{ mb: 3 }}
          >
            Meet the dedicated members of our association.
          </Typography>
        </Box>

        {/* Search Bar */}
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center', gap: 2 }}>
          <TextField
            select
            size="small"
            value={searchField}
            onChange={(e) => setSearchField(e.target.value)}
            sx={{ width: 150 }}
          >
            <MenuItem value="name">Name</MenuItem>
            <MenuItem value="enrollmentNo">Enrollment No</MenuItem>
            <MenuItem value="mobileNo">Mobile No</MenuItem>
          </TextField>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            placeholder={`Search by ${searchField}...`}
            value={searchTerm}
            onChange={handleSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'text.secondary', fontSize: '1.2rem' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              maxWidth: 500,
              '& .MuiOutlinedInput-root': {
                bgcolor: 'background.paper',
              }
            }}
          />
        </Box>

        {/* Table */}
        <Paper 
          elevation={0}
          sx={{ 
            overflow: 'hidden',
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 1,
          }}
        >
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell 
                    sx={{ 
                      fontWeight: 600, 
                      bgcolor: alpha(theme.palette.primary.main, 0.03),
                      width: '10%',
                      display: { xs: 'none', sm: 'table-cell' }
                    }}
                  >
                    Sr. No.
                  </TableCell>
                  <TableCell 
                    sx={{ 
                      fontWeight: 600, 
                      bgcolor: alpha(theme.palette.primary.main, 0.03),
                      width: '40%'
                    }}
                  >
                    Name
                  </TableCell>
                  <TableCell 
                    sx={{ 
                      fontWeight: 600, 
                      bgcolor: alpha(theme.palette.primary.main, 0.03),
                      width: '30%',
                      display: { xs: 'none', sm: 'table-cell' }
                    }}
                  >
                    Enrollment No
                  </TableCell>
                  <TableCell 
                    sx={{ 
                      fontWeight: 600, 
                      bgcolor: alpha(theme.palette.primary.main, 0.03),
                      width: '20%'
                    }}
                  >
                    Date
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2 }}>
                        <CircularProgress size={20} sx={{ color: '#580100' }} />
                        <Typography color="text.secondary">
                          Loading members...
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  <>
                    {filteredMembers
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((member, index) => (
                        <TableRow 
                          key={member.id} 
                          hover 
                          onClick={() => handleRowClick(member)}
                          sx={{ 
                            cursor: 'pointer',
                            '&:hover': {
                              bgcolor: alpha(theme.palette.primary.main, 0.04)
                            }
                          }}
                        >
                          <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                            {page * rowsPerPage + index + 1}
                          </TableCell>
                          <TableCell component="th" scope="row" sx={{ color: '#580100', fontWeight: 500 }}>
                            {member.name}
                          </TableCell>
                          <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                            {maskEnrollmentNo(member.enrollmentNo)}
                          </TableCell>
                          <TableCell>
                            {member.dateOfEnrollment}
                          </TableCell>
                        </TableRow>
                      ))}
                    {!loading && filteredMembers.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                          <Typography color="text.secondary">
                            No members found
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {!loading && filteredMembers.length > 0 && (
            <TablePagination
              component="div"
              count={filteredMembers.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{
                borderTop: `1px solid ${theme.palette.divider}`,
              }}
            />
          )}
        </Paper>

        {/* Member Details Dialog */}
        <Dialog
          open={detailsOpen}
          onClose={() => setDetailsOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            elevation: 0,
            sx: {
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider'
            }
          }}
        >
          <DialogTitle 
            sx={{ 
              pb: 2,
              pr: 6,
              borderBottom: '1px solid',
              borderColor: 'divider'
            }}
          >
            <Typography variant="h6" sx={{ color: '#580100', fontWeight: 600 }}>
              Member Details
            </Typography>
            <IconButton
              onClick={() => setDetailsOpen(false)}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: 'text.secondary'
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ py: 3 }}>
            {selectedMember && (
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="h5" sx={{ color: '#580100', fontWeight: 600, mb: 1 }}>
                    {selectedMember.name}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Enrollment Number
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {maskEnrollmentNo(selectedMember.enrollmentNo)}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Date of Enrollment
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {selectedMember.dateOfEnrollment}
                  </Typography>
                </Grid>
                
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Address
                  </Typography>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      fontWeight: 500,
                      bgcolor: alpha(theme.palette.primary.main, 0.03),
                      p: 2,
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'divider'
                    }}
                  >
                    {selectedMember.address}
                  </Typography>
                </Grid>
                
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Mobile Number
                  </Typography>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      fontWeight: 500,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 1,
                      bgcolor: alpha(theme.palette.primary.main, 0.03),
                      py: 1,
                      px: 2,
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'divider'
                    }}
                  >
                    {selectedMember.mobileNo}
                  </Typography>
                </Grid>
              </Grid>
            )}
          </DialogContent>
        </Dialog>
      </Container>
    </Box>
  );
}

export default Members;
